import { useState, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell,
    Legend, AreaChart, Area
} from "recharts";
import API from "../../services/apinode";
import Navbar from '../../components/navbar/navbar'

// ─── Formatters ──────────────────────────────────────────
const fmt = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtNum = (v) => Number(v || 0).toLocaleString("pt-BR");
const fmtData = (d) => { if (!d) return "—"; const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleDateString("pt-BR"); };
const fmtHora = (d) => { if (!d) return "—"; const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); };

const STATUS_COR = {
    paid: { bg: "#d1fae5", text: "#065f46", label: "Pago" },
    pending: { bg: "#fef3c7", text: "#92400e", label: "Pendente" },
    cancelled: { bg: "#fee2e2", text: "#991b1b", label: "Cancelado" },
    canceled: { bg: "#fee2e2", text: "#991b1b", label: "Cancelado" },
    cancelado: { bg: "#fee2e2", text: "#991b1b", label: "Cancelado" },
    delivered: { bg: "#dbeafe", text: "#1e40af", label: "Entregue" },
    shipped: { bg: "#ede9fe", text: "#5b21b6", label: "Enviado" },
    processing: { bg: "#ffedd5", text: "#9a3412", label: "Processando" },
};

const CORES_GRAFICO = ["#2563eb", "#059669", "#7c3aed", "#dc2626", "#0891b2", "#f59e0b"];

function StatusBadge({ status }) {
    const key = status?.toLowerCase()?.trim();
    const s = STATUS_COR[key] || { bg: "#f3f4f6", text: "#374151", label: status };
    return (
        <span style={{ background: s.bg, color: s.text, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
            {s.label || status || "—"}
        </span>
    );
}

// ─── Cards ───────────────────────────────────────────────
function MetricCard({ icon, label, valor, sub, cor, delta }) {
    const isPos = delta > 0;
    return (
        <div style={{ background: "#fff", borderRadius: 14, padding: "16px 20px", boxShadow: "0 1px 4px rgba(0,0,0,.07)", borderTop: `3px solid ${cor}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: .5 }}>{label}</div>
                {delta !== undefined && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: isPos ? "#059669" : "#dc2626", background: isPos ? "#d1fae5" : "#fee2e2", padding: "2px 6px", borderRadius: 8 }}>
                        {isPos ? "+" : ""}{delta?.toFixed(1)}%
                    </span>
                )}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: "6px 0 4px" }}>{valor}</div>
            {sub && <div style={{ fontSize: 12, color: "#9ca3af" }}>{sub}</div>}
        </div>
    );
}

// ─── Tooltip customizado ─────────────────────────────────
function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 16px", fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,.1)" }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: "#111" }}>{label}</div>
            {payload.map(p => (
                <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
                    <span style={{ fontWeight: 600 }}>{p.name}:</span>{" "}
                    {p.name.toLowerCase().includes("valor") || p.name.toLowerCase().includes("ticket") ? fmt(p.value) : fmtNum(p.value)}
                </div>
            ))}
        </div>
    );
}

// ─── Exportação Excel BI ─────────────────────────────────
function exportarExcel(dados, resumo, grafico) {
    const wb = XLSX.utils.book_new();

    // ABA 1: Pedidos detalhados com formatação
    const pedidosData = [
        ["RELATÓRIO DE VENDAS - EXPORTADO EM " + new Date().toLocaleString("pt-BR")],
        [],
        ["Pedido", "Cliente", "CPF", "Data", "Hora", "Valor", "Frete", "Desconto", "Status", "Situação", "NF", "Loja", "Vendedor", "Tipo"],
        ...dados.map(p => [
            p.PEDIDO, p.CLIENTE || "", p.CPF || "",
            fmtData(p.DATA), p.HORA || fmtHora(p.DATA),
            Number(p.VALOR || 0), Number(p.VALOR_FRETE || 0), Number(p.DESCONTO || 0),
            p.STATUS || "", p.SITUACAO || "", p.NOTAFISCAL || "",
            p.ID_LOJA || "", p.NOME_VENDEDOR || "",
            p.IS_FULFILLMENT === "S" ? "Fulfillment" : "Normal"
        ])
    ];
    const wsPedidos = XLSX.utils.aoa_to_sheet(pedidosData);

    // Estilo cabeçalho
    wsPedidos["A1"] = { v: pedidosData[0][0], t: "s", s: { font: { bold: true, sz: 12 }, fill: { fgColor: { rgb: "1E3A5F" } }, color: { rgb: "FFFFFF" } } };

    // Larguras colunas
    wsPedidos["!cols"] = [
        { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 8 },
        { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
        { wch: 15 }, { wch: 10 }, { wch: 20 }, { wch: 12 }
    ];
    XLSX.utils.book_append_sheet(wb, wsPedidos, "Pedidos");

    // ABA 2: Resumo executivo
    if (resumo) {
        const resumoData = [
            ["RESUMO EXECUTIVO"],
            [],
            ["Indicador", "Valor"],
            ["Total de Pedidos", resumo.totalPedidos || 0],
            ["Faturamento Total", Number(resumo.totalValor || 0)],
            ["Ticket Médio", Number(resumo.ticketMedio || 0)],
            ["Total Frete", Number(resumo.totalFrete || 0)],
            ["Total Descontos", Number(resumo.totalDesconto || 0)],
            ["Pedidos Cancelados", resumo.totalCancelados || 0],
            ["Valor Cancelados", Number(resumo.valorCancelados || 0)],
            [],
            ["FULFILLMENT vs NORMAL"],
            ["", "Qtd", "Valor", "Frete"],
            ["Fulfillment", resumo.fulfillment?.total || 0, Number(resumo.fulfillment?.valor || 0), Number(resumo.fulfillment?.frete || 0)],
            ["Normal", resumo.normal?.total || 0, Number(resumo.normal?.valor || 0), Number(resumo.normal?.frete || 0)],
        ];
        const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
        wsResumo["!cols"] = [{ wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo Executivo");
    }

    // ABA 3: Gráfico por dia (dados brutos para gráfico no Excel)
    if (grafico?.length) {
        const graficoData = [
            ["VENDAS POR DIA"],
            [],
            ["Data", "Valor Total", "Qtd Total", "Valor Fulfillment", "Qtd Fulfillment", "Valor Normal", "Qtd Normal"],
            ...grafico.map(g => [
                g.data,
                Number(g.valor || 0), Number(g.qtd || 0),
                Number(g.valorFulfillment || 0), Number(g.qtdFulfillment || 0),
                Number(g.valorNormal || 0), Number(g.qtdNormal || 0),
            ])
        ];
        const wsGrafico = XLSX.utils.aoa_to_sheet(graficoData);
        wsGrafico["!cols"] = [{ wch: 12 }, { wch: 16 }, { wch: 10 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, wsGrafico, "Dados por Dia");
    }

    // ABA 4: Top Clientes
    const topClientes = [...dados]
        .filter(p => !["canceled", "cancelled", "cancelado"].includes(p.STATUS?.toLowerCase()))
        .reduce((acc, p) => {
            const key = p.CLIENTE || "Desconhecido";
            if (!acc[key]) acc[key] = { cliente: key, pedidos: 0, valor: 0 };
            acc[key].pedidos++;
            acc[key].valor += Number(p.VALOR || 0);
            return acc;
        }, {});

    const topClientesArr = Object.values(topClientes)
        .sort((a, b) => b.valor - a.valor)
        .slice(0, 50);

    const wsClientes = XLSX.utils.aoa_to_sheet([
        ["TOP 50 CLIENTES POR FATURAMENTO"],
        [],
        ["#", "Cliente", "Pedidos", "Valor Total", "Ticket Médio"],
        ...topClientesArr.map((c, i) => [
            i + 1, c.cliente, c.pedidos,
            Number(c.valor.toFixed(2)),
            Number((c.valor / c.pedidos).toFixed(2))
        ])
    ]);
    wsClientes["!cols"] = [{ wch: 5 }, { wch: 30 }, { wch: 10 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsClientes, "Top Clientes");

    // Salvar
    XLSX.writeFile(wb, `BI_Vendas_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ─── Hook principal ──────────────────────────────────────
function useRelatorio(conta) {
    const idusuario = localStorage.getItem("idusuario");
    const hoje = new Date().toISOString().slice(0, 10);
    const primeiroDia = hoje.slice(0, 7) + "-01";

    const [filtros, setFiltros] = useState({ dataInicio: primeiroDia, dataFim: hoje, status: "", situacao: "", idLoja: "", vendedor: "", busca: "", fulfillment: "" });
    const [dados, setDados] = useState([]);
    const [resumo, setResumo] = useState(null);
    const [grafico, setGrafico] = useState([]);
    const [lojas, setLojas] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [pagina, setPagina] = useState(1);
    const [total, setTotal] = useState(0);
    const POR_PAGINA = 50;

    useEffect(() => {
        if (!conta || !idusuario) return;
        API.get(`/relatorio/opcoes/${conta}`, { params: { idusuario } })
            .then(r => { setLojas(r.data.lojas || []); setVendedores(r.data.vendedores || []); })
            .catch(e => setErro(e.response?.data?.message || "Erro ao carregar permissões."));
    }, [conta, idusuario]);

    const buscar = useCallback(async (pg = 1) => {
        if (!conta || !idusuario) return;
        setCarregando(true); setErro(null);
        try {
            const params = { ...filtros, pagina: pg, porPagina: POR_PAGINA, idusuario };
            const [resDados, resResumo, resGrafico] = await Promise.all([
                API.get(`/relatorio/vendas/${conta}`, { params }),
                API.get(`/relatorio/vendas/resumo/${conta}`, { params }),
                API.get(`/relatorio/vendas/grafico/${conta}`, { params }),
            ]);
            setDados(resDados.data.pedidos || []);
            setTotal(resDados.data.total || 0);
            setResumo(resResumo.data);
            setGrafico(resGrafico.data || []);
            setPagina(pg);
        } catch (e) {
            setErro(e.response?.data?.message || e.message || "Erro ao carregar.");
        } finally { setCarregando(false); }
    }, [conta, filtros]);

    useEffect(() => { if (conta && idusuario) buscar(1); }, [conta]);

    return { filtros, setFiltros, dados, resumo, grafico, lojas, vendedores, carregando, erro, pagina, total, POR_PAGINA, buscar };
}

// ─── Componente BI ───────────────────────────────────────
function PainelBI({ dados, resumo, grafico }) {
    // Top 5 vendedores
    const topVendedores = useMemo(() => {
        const map = {};
        dados.filter(p => !["canceled", "cancelled", "cancelado"].includes(p.STATUS?.toLowerCase()))
            .forEach(p => {
                const k = p.NOME_VENDEDOR || "Sem vendedor";
                if (!map[k]) map[k] = { nome: k, valor: 0, qtd: 0 };
                map[k].valor += Number(p.VALOR || 0);
                map[k].qtd++;
            });
        return Object.values(map).sort((a, b) => b.valor - a.valor).slice(0, 5);
    }, [dados]);

    // Distribuição por status
    const porStatus = useMemo(() => {
        const map = {};
        dados.forEach(p => {
            const k = STATUS_COR[p.STATUS?.toLowerCase()]?.label || p.STATUS || "Outros";
            if (!map[k]) map[k] = 0;
            map[k]++;
        });
        return Object.entries(map).map(([name, value]) => ({ name, value }));
    }, [dados]);

    // Distribuição por forma de pagamento
    const porPgto = useMemo(() => {
        const map = {};
        dados.filter(p => !["canceled", "cancelled", "cancelado"].includes(p.STATUS?.toLowerCase()))
            .forEach(p => {
                const k = p.FORMA_PGTO || p.STATUS || "Outros";
                if (!map[k]) map[k] = 0;
                map[k] += Number(p.VALOR || 0);
            });
        return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    }, [dados]);

    if (!dados.length) return null;

    return (

        <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: "#2563eb", color: "#fff", padding: "2px 10px", borderRadius: 8, fontSize: 12 }}>BI</span>
                Análise de Inteligência de Negócios
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>

                {/* Gráfico de área — tendência */}
                {grafico.length > 0 && (
                    <div style={{ gridColumn: "span 2", background: "#fff", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,.07)" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 14 }}>Tendência de faturamento</div>
                        <ResponsiveContainer width="100%" height={180}>
                            <AreaChart data={grafico}>
                                <defs>
                                    <linearGradient id="colorValor" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="data" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                                <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} tickFormatter={v => "R$" + Number(v / 1000).toFixed(0) + "k"} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="valor" name="Valor Total" stroke="#2563eb" strokeWidth={2} fill="url(#colorValor)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Pizza — por status */}
                <div style={{ background: "#fff", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,.07)" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 14 }}>Distribuição por status</div>
                    <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                            <Pie data={porStatus} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                                {porStatus.map((_, i) => <Cell key={i} fill={CORES_GRAFICO[i % CORES_GRAFICO.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v) => fmtNum(v) + " pedidos"} />
                        </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        {porStatus.map((s, i) => (
                            <span key={s.name} style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4, color: "#6b7280" }}>
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: CORES_GRAFICO[i % CORES_GRAFICO.length], display: "inline-block" }}></span>
                                {s.name} ({fmtNum(s.value)})
                            </span>
                        ))}
                    </div>
                </div>

                {/* Top vendedores */}
                <div style={{ background: "#fff", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,.07)" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 14 }}>Top 5 vendedores</div>
                    {topVendedores.map((v, i) => {
                        const max = topVendedores[0]?.valor || 1;
                        const pct = (v.valor / max) * 100;
                        return (
                            <div key={v.nome} style={{ marginBottom: 10 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                        <span style={{ color: "#9ca3af", marginRight: 4 }}>#{i + 1}</span>{v.nome}
                                    </span>
                                    <span style={{ fontSize: 11, color: "#2563eb", fontWeight: 700 }}>{fmt(v.valor)}</span>
                                </div>
                                <div style={{ height: 4, background: "#f3f4f6", borderRadius: 4 }}>
                                    <div style={{ height: 4, background: CORES_GRAFICO[i], borderRadius: 4, width: pct + "%" }}></div>
                                </div>
                                <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>{fmtNum(v.qtd)} pedidos</div>
                            </div>
                        );
                    })}
                </div>

                {/* Fulfillment vs Normal comparativo */}
                {resumo && (
                    <div style={{ gridColumn: "span 2", background: "#fff", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,.07)" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 14 }}>Fulfillment vs Normal — por dia</div>
                        <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={grafico} barCategoryGap="30%">
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="data" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                                <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} tickFormatter={v => "R$" + Number(v / 1000).toFixed(0) + "k"} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="valorFulfillment" name="Fulfillment" fill="#7c3aed" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="valorNormal" name="Normal" fill="#059669" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Por forma de pagamento */}
                <div style={{ background: "#fff", borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,.07)" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 14 }}>Por forma de pagamento</div>
                    {porPgto.slice(0, 5).map((p, i) => {
                        const max = porPgto[0]?.value || 1;
                        return (
                            <div key={p.name} style={{ marginBottom: 10 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                    <span style={{ fontSize: 12, color: "#374151" }}>{p.name}</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: "#059669" }}>{fmt(p.value)}</span>
                                </div>
                                <div style={{ height: 4, background: "#f3f4f6", borderRadius: 4 }}>
                                    <div style={{ height: 4, background: CORES_GRAFICO[i], borderRadius: 4, width: (p.value / max * 100) + "%" }}></div>
                                </div>
                            </div>
                        );
                    })}
                </div>

            </div>
        </div>
    );
}

// ─── Componente principal ────────────────────────────────
export default function RelatorioVendas({ conta }) {
    const { filtros, setFiltros, dados, resumo, grafico, lojas, vendedores, carregando, erro, pagina, total, POR_PAGINA, buscar } = useRelatorio(conta);

    const [aba, setAba] = useState("tabela"); // tabela | bi
    const [exportando, setExportando] = useState(false);
    const [colunasVisiveis, setColunasVisiveis] = useState({
        pedido: true, cliente: true, valor: true, status: true, situacao: true,
        data: true, hora: true, notafiscal: true, chaveacesso: false,
        frete: true, desconto: true, idloja: true, vendedor: true, fulfillment: true,
    });
    const [mostrarColunas, setMostrarColunas] = useState(false);

    const totalPaginas = Math.ceil(total / POR_PAGINA);
    const atualizarFiltro = (campo, valor) => setFiltros(prev => ({ ...prev, [campo]: valor }));
    const handleBuscar = (e) => { e.preventDefault(); buscar(1); };

    const handleExportarExcel = async () => {
        if (!conta || exportando) return;
        setExportando(true);
        try {
            const idusuario = localStorage.getItem("idusuario");
            // Busca TODOS os registros sem paginação
            const params = { ...filtros, pagina: 1, porPagina: 99999, idusuario };
            const [resTodos, resResumoExport, resGraficoExport] = await Promise.all([
                API.get(`/relatorio/vendas/${conta}`, { params }),
                API.get(`/relatorio/vendas/resumo/${conta}`, { params }),
                API.get(`/relatorio/vendas/grafico/${conta}`, { params }),
            ]);
            const todosDados = resTodos.data.pedidos || [];
            exportarExcel(todosDados, resResumoExport.data, resGraficoExport.data || []);
        } catch (e) {
            alert("Erro ao exportar: " + e.message);
        } finally {
            setExportando(false);
        }
    };

    return (

        <>
            <Navbar />
            <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px 28px", fontFamily: "system-ui, sans-serif" }}>

                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
                    <div>
                        <h3 style={{ margin: 0, fontWeight: 700, fontSize: 22, color: "#111827" }}>
                            <i className="bi bi-graph-up-arrow" style={{ color: "#2563eb", marginRight: 10 }} />
                            Relatório de Vendas
                        </h3>
                        <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>Análise de pedidos por período, conta e situação</p>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        {/* Botão BI */}
                        <button onClick={() => setAba(aba === "bi" ? "tabela" : "bi")} style={{
                            display: "flex", alignItems: "center", gap: 8,
                            background: aba === "bi" ? "#7c3aed" : "#fff", color: aba === "bi" ? "#fff" : "#7c3aed",
                            border: "1.5px solid #7c3aed", borderRadius: 10, padding: "10px 20px",
                            fontWeight: 600, fontSize: 13, cursor: "pointer"
                        }}>
                            <i className="bi bi-bar-chart-line" /> {aba === "bi" ? "Ver Tabela" : "Painel BI"}
                        </button>

                        {/* Exportar Excel BI */}
                        <button onClick={handleExportarExcel} disabled={exportando || !total} style={{
                            display: "flex", alignItems: "center", gap: 8,
                            background: exportando ? "#6b7280" : total ? "#059669" : "#9ca3af", color: "#fff",
                            border: "none", borderRadius: 10, padding: "10px 20px",
                            fontWeight: 600, fontSize: 13, cursor: exportando || !total ? "not-allowed" : "pointer"
                        }}>
                            {exportando
                                ? <><span className="spinner-border spinner-border-sm" /> Baixando todos ({fmtNum(total)})…</>
                                : <><i className="bi bi-file-earmark-excel" /> Exportar Excel BI ({fmtNum(total)})</>}
                        </button>
                    </div>
                </div>

                {/* Filtros */}
                <div style={{ background: "#fff", borderRadius: 14, padding: "18px 22px", boxShadow: "0 1px 4px rgba(0,0,0,.06)", marginBottom: 20 }}>
                    <form onSubmit={handleBuscar}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
                            <div><label style={lbStyle}>Data início</label><input type="date" style={inputStyle} value={filtros.dataInicio} onChange={e => atualizarFiltro("dataInicio", e.target.value)} /></div>
                            <div><label style={lbStyle}>Data fim</label><input type="date" style={inputStyle} value={filtros.dataFim} onChange={e => atualizarFiltro("dataFim", e.target.value)} /></div>
                            <div>
                                <label style={lbStyle}>Status</label>
                                <select style={inputStyle} value={filtros.status} onChange={e => atualizarFiltro("status", e.target.value)}>
                                    <option value="">Todos</option>
                                    <option value="paid">Pago</option><option value="pending">Pendente</option>
                                    <option value="cancelled">Cancelado</option><option value="delivered">Entregue</option>
                                    <option value="shipped">Enviado</option><option value="processing">Processando</option>
                                </select>
                            </div>
                            <div>
                                <label style={lbStyle}>Situação</label>
                                <select style={inputStyle} value={filtros.situacao} onChange={e => atualizarFiltro("situacao", e.target.value)}>
                                    {["", "Pendente", "Aprovado", "Cancelado", "Entregue", "Devolvido"].map(s => <option key={s} value={s}>{s || "Todas"}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={lbStyle}>Conta / Loja</label>
                                <select style={inputStyle} value={filtros.idLoja} onChange={e => atualizarFiltro("idLoja", e.target.value)}>
                                    <option value="">Todas</option>
                                    {lojas.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={lbStyle}>Vendedor</label>
                                <select style={inputStyle} value={filtros.vendedor} onChange={e => atualizarFiltro("vendedor", e.target.value)}>
                                    <option value="">Todos</option>
                                    {vendedores.map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={lbStyle}>Tipo entrega</label>
                                <select style={inputStyle} value={filtros.fulfillment} onChange={e => atualizarFiltro("fulfillment", e.target.value)}>
                                    <option value="">Todos</option><option value="S">Fulfillment</option><option value="N">Normal</option>
                                </select>
                            </div>
                            <div><label style={lbStyle}>Busca</label><input type="text" style={inputStyle} placeholder="Pedido/cliente/NF..." value={filtros.busca} onChange={e => atualizarFiltro("busca", e.target.value)} /></div>
                            <div style={{ display: "flex", alignItems: "flex-end" }}>
                                <button type="submit" disabled={carregando} style={{ width: "100%", background: "#2563eb", color: "#fff", border: "none", borderRadius: 9, padding: "9px 0", fontWeight: 600, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                                    {carregando ? <><span className="spinner-border spinner-border-sm" /> Buscando…</> : <><i className="bi bi-search" /> Buscar</>}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                {erro && <div className="alert alert-danger d-flex align-items-center gap-2" style={{ borderRadius: 12 }}><i className="bi bi-exclamation-triangle-fill" /> {erro}</div>}

                {/* Cards resumo */}
                {resumo && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14, marginBottom: 20 }}>
                        <MetricCard icon="bag-check" label="Total Pedidos" valor={fmtNum(resumo.totalPedidos)} sub={`${fmtNum(resumo.totalCancelados)} cancelado(s)`} cor="#2563eb" />
                        <MetricCard icon="cash-stack" label="Faturamento" valor={fmt(resumo.totalValor)} sub="excl. cancelados" cor="#059669" />
                        <MetricCard icon="arrow-repeat" label="Ticket Médio" valor={fmt(resumo.ticketMedio)} sub="por pedido" cor="#0891b2" />
                        <MetricCard icon="truck" label="Total Frete" valor={fmt(resumo.totalFrete)} sub="só ML" cor="#7c3aed" />
                        <MetricCard icon="tag" label="Descontos" valor={fmt(resumo.totalDesconto)} sub="concedidos" cor="#dc2626" />
                        <MetricCard icon="x-circle" label="Cancelados" valor={fmtNum(resumo.totalCancelados)} sub={fmt(resumo.valorCancelados)} cor="#f59e0b" />
                    </div>
                )}

                {/* Conteúdo principal — BI ou Tabela */}
                {aba === "bi" ? (
                    <PainelBI dados={dados} resumo={resumo} grafico={grafico} />
                ) : (
                    <>
                        {/* Gráfico diário */}
                        {grafico.length > 0 && (
                            <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,.06)", marginBottom: 20 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 16 }}>Vendas por dia</div>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={grafico} barCategoryGap="30%">
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="data" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} tickFormatter={v => fmt(v).replace("R$", "").trim()} />
                                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                        <Bar yAxisId="left" dataKey="valor" name="Valor Total" fill="#2563eb" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {/* Tabela */}
                        <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,.06)", overflow: "hidden" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #f3f4f6", flexWrap: "wrap", gap: 10 }}>
                                <span style={{ fontSize: 13, color: "#6b7280" }}>{carregando ? "Buscando…" : `${fmtNum(total)} pedido(s)`}</span>
                                <div style={{ position: "relative" }}>
                                    <button onClick={() => setMostrarColunas(v => !v)} style={{ display: "flex", alignItems: "center", gap: 6, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer", color: "#374151" }}>
                                        <i className="bi bi-layout-three-columns" /> Colunas
                                    </button>
                                    {mostrarColunas && (
                                        <div style={{ position: "absolute", right: 0, top: "110%", zIndex: 50, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 16px", boxShadow: "0 8px 24px rgba(0,0,0,.12)", minWidth: 220, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
                                            {Object.entries({ pedido: "Pedido", cliente: "Cliente", valor: "Valor", status: "Status", situacao: "Situação", data: "Data", hora: "Hora", notafiscal: "NF", chaveacesso: "Chave", frete: "Frete", desconto: "Desconto", idloja: "Loja", vendedor: "Vendedor", fulfillment: "Tipo" }).map(([k, v]) => (
                                                <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                                                    <input type="checkbox" checked={colunasVisiveis[k]} onChange={() => setColunasVisiveis(prev => ({ ...prev, [k]: !prev[k] }))} /> {v}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ background: "#f9fafb" }}>
                                            {colunasVisiveis.pedido && <th style={thStyle}>Pedido</th>}
                                            {colunasVisiveis.cliente && <th style={thStyle}>Cliente</th>}
                                            {colunasVisiveis.data && <th style={thStyle}>Data</th>}
                                            {colunasVisiveis.hora && <th style={thStyle}>Hora</th>}
                                            {colunasVisiveis.fulfillment && <th style={thStyle}>Tipo</th>}
                                            {colunasVisiveis.valor && <th style={{ ...thStyle, textAlign: "right" }}>Valor</th>}
                                            {colunasVisiveis.frete && <th style={{ ...thStyle, textAlign: "right" }}>Frete</th>}
                                            {colunasVisiveis.desconto && <th style={{ ...thStyle, textAlign: "right" }}>Desconto</th>}
                                            {colunasVisiveis.status && <th style={thStyle}>Status</th>}
                                            {colunasVisiveis.situacao && <th style={thStyle}>Situação</th>}
                                            {colunasVisiveis.notafiscal && <th style={thStyle}>NF</th>}
                                            {colunasVisiveis.chaveacesso && <th style={thStyle}>Chave</th>}
                                            {colunasVisiveis.idloja && <th style={thStyle}>Loja</th>}
                                            {colunasVisiveis.vendedor && <th style={thStyle}>Vendedor</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {carregando && <tr><td colSpan={20} style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}><div className="spinner-border text-primary" /><div style={{ marginTop: 10 }}>Carregando…</div></td></tr>}
                                        {!carregando && dados.length === 0 && <tr><td colSpan={20} style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}><i className="bi bi-inbox fs-2 d-block mb-2" />Nenhum pedido encontrado</td></tr>}
                                        {!carregando && dados.map((p, i) => {
                                            const isCancelado = ["canceled", "cancelled", "cancelado", "cancelada"].includes(p.STATUS?.toLowerCase()?.trim()) || ["cancelado", "cancelada"].includes(p.SITUACAO?.toLowerCase()?.trim());
                                            const bg = isCancelado ? (i % 2 === 0 ? "#fff5f5" : "#fff0f0") : p.IS_FULFILLMENT === "S" ? (i % 2 === 0 ? "#faf5ff" : "#f5f0fe") : (i % 2 === 0 ? "#fff" : "#fafafa");
                                            return (
                                                <tr key={p.PEDIDO ?? i} style={{ borderBottom: "1px solid #f3f4f6", background: bg, transition: "background .1s" }}
                                                    onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                                                    onMouseLeave={e => e.currentTarget.style.background = bg}>
                                                    {colunasVisiveis.pedido && <td style={tdStyle}><span style={{ fontWeight: 700, color: "#2563eb" }}>{p.PEDIDO}</span>{p.IDPEDIDOLOJA && <div style={{ fontSize: 11, color: "#9ca3af" }}>{p.IDPEDIDOLOJA}</div>}</td>}
                                                    {colunasVisiveis.cliente && <td style={tdStyle}><div style={{ fontWeight: 500 }}>{p.CLIENTE || "—"}</div>{p.CPF && <div style={{ fontSize: 11, color: "#9ca3af" }}>{p.CPF}</div>}</td>}
                                                    {colunasVisiveis.data && <td style={tdStyle}>{fmtData(p.DATA)}</td>}
                                                    {colunasVisiveis.hora && <td style={tdStyle}>{p.HORA || fmtHora(p.DATA)}</td>}
                                                    {colunasVisiveis.fulfillment && <td style={tdStyle}><span style={{ background: p.IS_FULFILLMENT === "S" ? "#ede9fe" : "#f0fdf4", color: p.IS_FULFILLMENT === "S" ? "#5b21b6" : "#15803d", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{p.IS_FULFILLMENT === "S" ? "Full" : "Normal"}</span></td>}
                                                    {colunasVisiveis.valor && <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: isCancelado ? "#dc2626" : "#059669" }}>{fmt(p.VALOR)}</td>}
                                                    {colunasVisiveis.frete && <td style={{ ...tdStyle, textAlign: "right" }}>{fmt(p.VALOR_FRETE)}</td>}
                                                    {colunasVisiveis.desconto && <td style={{ ...tdStyle, textAlign: "right", color: p.DESCONTO > 0 ? "#dc2626" : "#9ca3af" }}>{fmt(p.DESCONTO)}</td>}
                                                    {colunasVisiveis.status && <td style={tdStyle}><StatusBadge status={p.STATUS} /></td>}
                                                    {colunasVisiveis.situacao && <td style={tdStyle}>{p.SITUACAO || "—"}</td>}
                                                    {colunasVisiveis.notafiscal && <td style={tdStyle}><span style={{ fontFamily: "monospace", fontSize: 12 }}>{p.NOTAFISCAL || "—"}</span></td>}
                                                    {colunasVisiveis.chaveacesso && <td style={tdStyle}><span style={{ fontFamily: "monospace", fontSize: 10, color: "#6b7280" }}>{p.CHAVEACESSO ? p.CHAVEACESSO.slice(0, 20) + "…" : "—"}</span></td>}
                                                    {colunasVisiveis.idloja && <td style={tdStyle}>{p.ID_LOJA || "—"}</td>}
                                                    {colunasVisiveis.vendedor && <td style={tdStyle}>{p.NOME_VENDEDOR || "—"}</td>}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {totalPaginas > 1 && (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #f3f4f6", flexWrap: "wrap", gap: 8 }}>
                                    <span style={{ fontSize: 13, color: "#6b7280" }}>Página {pagina} de {totalPaginas}</span>
                                    <div style={{ display: "flex", gap: 6 }}>
                                        {[["«", 1], ["‹", pagina - 1]].map(([l, p]) => <PagBtn key={l} label={l} onClick={() => buscar(p)} disabled={pagina === 1} />)}
                                        {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => { const pg = Math.max(1, pagina - 2) + i; if (pg > totalPaginas) return null; return <PagBtn key={pg} label={pg} onClick={() => buscar(pg)} ativo={pg === pagina} />; })}
                                        {[["›", pagina + 1], ["»", totalPaginas]].map(([l, p]) => <PagBtn key={l} label={l} onClick={() => buscar(p)} disabled={pagina === totalPaginas} />)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

function PagBtn({ label, onClick, disabled, ativo }) {
    return (
        <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, border: ativo ? "none" : "1px solid #e5e7eb", borderRadius: 8, background: ativo ? "#2563eb" : "#fff", color: ativo ? "#fff" : disabled ? "#d1d5db" : "#374151", cursor: disabled ? "not-allowed" : "pointer", fontSize: 13, fontWeight: ativo ? 700 : 400, display: "flex", alignItems: "center", justifyContent: "center" }}>{label}</button>
    );
}

const inputStyle = { width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 9, fontSize: 13, color: "#111827", background: "#fff", outline: "none", boxSizing: "border-box" };
const lbStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 5, textTransform: "uppercase", letterSpacing: .4 };
const thStyle = { padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: .5, whiteSpace: "nowrap", borderBottom: "1px solid #e5e7eb" };
const tdStyle = { padding: "10px 14px", color: "#374151", verticalAlign: "middle", whiteSpace: "nowrap" };